// Top section of the expanded activity dock panel: surfaces the active
// lead's capture checklist (contact / product / source / budget) so the
// rep can see at a glance what's still outstanding without leaving the
// dock. State machine, per docs/planning/org_activity_floating_dock.md §11:
//
//   1. No active lead → render nothing.
//   2. New lead appears AND has outstanding items → dispatch the dock-open
//      event so the rep sees the section the moment they open a lead.
//      Only triggers once per lead.id per tab session.
//   3. An item flips missing/warning → ok → add a 1-second background
//      flash class so the rep notices the green tick.
//   4. Active lead closes → linger for 1 second with a fade-out class on
//      the snapshot, then unmount.

import { useEffect, useMemo, useRef, useState } from "react";
import { useWorkspaceUrgencyContext } from "@/contexts/WorkspaceUrgencyContext";
import { ACTIVITY_DOCK_OPEN_EVENT } from "./ActivityFloatingDock";
import {
  computeLeadChecklistItems,
  hasOutstandingChecklistItems,
  type LeadChecklistItem,
  type LeadChecklistItemKey,
  type LeadChecklistItemStatus,
} from "@/components/lead/sections/leadChecklistItems";

const FLASH_DURATION_MS = 1000;
const FADE_OUT_DURATION_MS = 1000;

const STATUS_TEXT_COLOR: Record<LeadChecklistItemStatus, string> = {
  ok: "text-emerald-600 dark:text-emerald-400",
  missing: "text-rose-600 dark:text-rose-400",
  warning: "text-amber-600 dark:text-amber-400",
};

export type ActivityDockActiveLeadChecklistSectionProps = {
  /** WF-0 §6.1 — dismiss dock and continue capture in the leads panel. */
  onCloseDock?: () => void;
};

export function ActivityDockActiveLeadChecklistSection({
  onCloseDock,
}: ActivityDockActiveLeadChecklistSectionProps = {}) {
  const urgency = useWorkspaceUrgencyContext();
  const lead = urgency?.currentLead ?? null;
  const items = useMemo(
    () => computeLeadChecklistItems(lead?.extracted ?? null),
    [lead?.extracted],
  );

  // Track previous status per item key so we can detect newly-completed rows.
  const prevStatusRef = useRef<Map<LeadChecklistItemKey, LeadChecklistItemStatus>>(new Map());
  const [flashKeys, setFlashKeys] = useState<Set<LeadChecklistItemKey>>(new Set());

  // First-appearance auto-open: dispatch ACTIVITY_DOCK_OPEN_EVENT once per
  // lead.id per session, but only when the lead actually has outstanding
  // items. We snapshot the trigger lead-id rather than depending on
  // `items` so a transient race that briefly clears items doesn't cause
  // a re-trigger when they come back.
  const autoOpenedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!lead) return;
    if (autoOpenedRef.current.has(lead.id)) return;
    if (!hasOutstandingChecklistItems(items)) return;
    autoOpenedRef.current.add(lead.id);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(ACTIVITY_DOCK_OPEN_EVENT));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  // 1-second flash on status-flip → ok. We compute newly-ok keys by
  // comparing each render's items against the previous render's snapshot,
  // then schedule a timeout to clear the flash class.
  useEffect(() => {
    const newlyOk = new Set<LeadChecklistItemKey>();
    for (const item of items) {
      const prev = prevStatusRef.current.get(item.key);
      if (prev !== undefined && prev !== "ok" && item.status === "ok") {
        newlyOk.add(item.key);
      }
    }
    const nextSnapshot = new Map<LeadChecklistItemKey, LeadChecklistItemStatus>();
    for (const item of items) nextSnapshot.set(item.key, item.status);
    prevStatusRef.current = nextSnapshot;

    if (newlyOk.size === 0) return;
    setFlashKeys((prev) => {
      const merged = new Set(prev);
      for (const k of newlyOk) merged.add(k);
      return merged;
    });
    const timeout = window.setTimeout(() => {
      setFlashKeys((prev) => {
        const next = new Set(prev);
        for (const k of newlyOk) next.delete(k);
        return next;
      });
    }, FLASH_DURATION_MS);
    return () => window.clearTimeout(timeout);
  }, [items]);

  // Fade-out snapshot: when the active lead is closed (lead → null), keep
  // rendering the last-seen items for FADE_OUT_DURATION_MS so the section
  // can fade out smoothly instead of disappearing instantly.
  const [fadeSnapshot, setFadeSnapshot] = useState<LeadChecklistItem[] | null>(null);
  const prevLeadIdRef = useRef<string | null>(lead?.id ?? null);
  useEffect(() => {
    const currentId = lead?.id ?? null;
    const prevId = prevLeadIdRef.current;
    prevLeadIdRef.current = currentId;
    if (prevId !== null && currentId === null) {
      // Snapshot the items from the last render where the lead was non-null.
      // `items` here is computed from the now-null lead, so use the
      // previous-status snapshot to reconstruct shape.
      const snapshot: LeadChecklistItem[] = items.map((it) => ({
        ...it,
        status: prevStatusRef.current.get(it.key) ?? it.status,
      }));
      setFadeSnapshot(snapshot);
      const t = window.setTimeout(() => setFadeSnapshot(null), FADE_OUT_DURATION_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [lead?.id, items]);

  const showingFade = lead === null && fadeSnapshot !== null;
  const renderItems = showingFade ? fadeSnapshot! : lead ? items : null;
  if (!renderItems) return null;

  const containerClasses = [
    "mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-zinc-800/60",
    showingFade ? "activity-checklist-section-fade-out" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      aria-label="Active lead checklist"
      className={containerClasses}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
          Current lead
        </h3>
        <div className="flex min-w-0 items-center gap-2">
          {!showingFade && lead ? (
            <span className="truncate max-w-[120px] text-[11px] text-gray-500 dark:text-gray-400">
              {lead.name || "(unnamed)"}
            </span>
          ) : null}
          {onCloseDock ? (
            <button
              type="button"
              onClick={onCloseDock}
              className="shrink-0 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-zinc-900 dark:text-gray-200 dark:hover:bg-zinc-800"
            >
              Continue capture
            </button>
          ) : null}
        </div>
      </div>
      <ul className="space-y-0.5">
        {renderItems.map((item) => {
          const isFlashing = flashKeys.has(item.key);
          const itemClasses = [
            "rounded px-1.5 py-0.5 text-[12px] leading-snug transition-colors",
            STATUS_TEXT_COLOR[item.status],
            isFlashing ? "activity-checklist-item-flash" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={item.key} className={itemClasses}>
              {item.label}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
